import React, { useEffect, useState } from "react";
import { DataTable } from "@/components/ui/datatable";
import { incidentsColumnConfig } from "@/config/tableColumnConfig";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// DED-1141: Use FontAwesome duotone solid icons for actions column
import { faPenToSquare, faTrash } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import type { Incident, Audit } from "@/app/pages/auditcentre/index";
import type { ColumnDef } from "@tanstack/react-table";
import { IncidentDetailDialog } from "./IncidentDetailDialog";
import { IncidentCreateDialog } from "./IncidentCreateDialog";
import { IncidentEditDialog } from "./IncidentEditDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { useActiveTenancy } from "@/app/pages/auditcentre/useActiveTenancy";
import { AuditService } from "@/app/pages/auditcentre/auditService";
import { cleanDateAndTimeObject, toLocalDateString } from "@shared/renderDate";
import { useTenancy } from "@/contexts/TenancyContext";

type StandaloneIncidentTableProps = {
  incidents: (Incident & { auditId?: string })[];
  audits: Audit[];
  onIncidentCreated: (incident: Incident & { auditId?: string }) => Promise<boolean>;
  onIncidentUpdated?: (incidentId: string, updates: Partial<Incident>) => Promise<boolean>;
  onLink?: (incidentId: string, auditId: string) => Promise<boolean>;
  onUnlink?: (incidentId: string) => Promise<boolean>;
  onDelete?: (incidentId: string) => Promise<void>;
  deepLinkedIncidentId?: string;
};

export function StandaloneIncidentTable({ 
  incidents, 
  audits,
  onIncidentCreated,
  onIncidentUpdated,
  onLink,
  onUnlink,
  onDelete,
  deepLinkedIncidentId,
}: StandaloneIncidentTableProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [incidentToDelete, setIncidentToDelete] = useState<string | null>(null);
  const { userIdToName } = useTenancy();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<Incident & { auditId?: string } | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [incidentList, setIncidentList] = useState<(Incident & { auditId?: string })[]>(incidents);
  // Track recently-deleted IDs with timestamps to avoid transient reinsertions from upstream refreshes
  const [deletedIds, setDeletedIds] = useState<Map<string, number>>(new Map());

  // Deep-link open: if an id is provided and found in list, open view dialog
  useEffect(() => {
    if (!deepLinkedIncidentId || !Array.isArray(incidents) || incidents.length === 0) return;
    const match = incidents.find(i => String(i.id) === String(deepLinkedIncidentId));
    if (match) {
      setSelectedIncident(match);
      setDialogOpen(true);
    }
  }, [deepLinkedIncidentId, incidents]);

  // Keep local state in sync if parent props change (e.g., after external refresh/delete)
  useEffect(() => {
    const NOW = Date.now();
    const TTL_MS = 10_000; // 10 seconds guard window
    // Build a Set of active deleted IDs within TTL
    const activeDeleted = new Set<string>();
    const nextMap = new Map<string, number>();
    deletedIds.forEach((ts, id) => {
      if (NOW - ts < TTL_MS) {
        activeDeleted.add(id);
        nextMap.set(id, ts);
      }
    });
    // Filter out incidents that are within the guard window
    setIncidentList(incidents.filter(i => !activeDeleted.has(i.id)));
    // If we have pruned any expired entries, persist the cleaned map
    if (nextMap.size !== deletedIds.size) {
      setDeletedIds(nextMap);
    }
  }, [incidents, deletedIds]);

  // For audit linking
  const auditOptions = audits.map(a => ({ id: a.id, label: `${a.type}: ${a.scope}` }));

  const handleLink = async (incidentId: string, auditId: string) => {
    if (!onLink) return false;
    try {
      const success = await onLink(incidentId, auditId);
      if (success) {
        setIncidentList(list => list.map(i => i.id === incidentId ? { ...i, auditId } : i));
      }
      return success;
    } catch (error) {
      console.error('Error linking incident:', error);
      return false;
    }
  };

  const handleUnlink = async (incidentId: string) => {
    if (!onUnlink) return false;
    try {
      const success = await onUnlink(incidentId);
      if (success) {
        setIncidentList(list => list.map(i => i.id === incidentId ? { ...i, auditId: undefined } : i));
      }
      return success;
    } catch (error) {
      console.error('Error unlinking incident:', error);
      return false;
    }
  };
  const { id: activeTenancy } = useActiveTenancy();
  
  // DED-1141: Updated handleCreate to include new fields
  // DED-1339: Added controls for MCM integration
  async function handleCreate(newIncident: { 
    summary: string; 
    owner: string; 
    status: "Open" | "Closed"; 
    dateOfIncident?: Date | string;
    impact?: string;
    dateClosed?: Date | string;
    closeComment?: string;
    controls?: string[];
  }) {
    if (!activeTenancy) {
      console.error('No active tenancy ID found');
      return;
    }
    
    try {
      const auditService = new AuditService({ activeTenancy: { id: activeTenancy } });
      
      // DED-1141: Prepare the incident data with new fields, no auditId
      // DED-1339: Include controls for MCM integration
      const incidentData = {
        summary: newIncident.summary,
        owner: newIncident.owner,
        status: newIncident.status,
        ...(newIncident.dateOfIncident && { dateOfIncident: newIncident.dateOfIncident }),
        ...(newIncident.impact && { impact: newIncident.impact }),
        ...(newIncident.dateClosed && { dateClosed: newIncident.dateClosed }),
        ...(newIncident.closeComment && { closeComment: newIncident.closeComment }),
        ...(newIncident.controls && newIncident.controls.length > 0 && { controls: newIncident.controls }),
      };
      
      console.log('Creating incident with data:', incidentData);
      const createdIncident = await auditService.createIncident(incidentData);
      
      // Create a temporary incident with the data we have
      const tempIncident: Incident = {
        id: createdIncident.id || String(Date.now()),
        summary: newIncident.summary,
        owner: newIncident.owner,
        status: newIncident.status,
        createdAt: new Date(),
        updatedAt: new Date(),
        dateOfIncident: newIncident.dateOfIncident,
        impact: newIncident.impact,
        dateClosed: newIncident.dateClosed,
        closeComment: newIncident.closeComment,
        // DED-1339: Include controls in temp incident
        controls: newIncident.controls,
      };
      
      // Update local state
      setIncidentList(list => [...list, tempIncident]);
      
      // Notify parent component
      onIncidentCreated(tempIncident);
      
      console.log('Incident created successfully:', tempIncident);
    } catch (error) {
      console.error('Failed to create incident:', error);
    }
  }

  const columns = [
    {
      header: "Status",
      accessorKey: "status",
      cell: ({ row }) => (
        <Badge variant={row.original.status === "Open" ? "destructiveSubtle" : "successSubtle"}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      header: "Summary",
      accessorKey: "summary",
    },
    {
      header: "Owner",
      accessorKey: "owner",
      cell: ({ row }) => <span>{userIdToName[row.original.owner] === "" || userIdToName[row.original.owner] === undefined ? row.original.owner : userIdToName[row.original.owner]}</span>,
    },
    {
      header: "Date",
      accessorKey: "createdAt",
      cell: ({ row }) => {
        const timestamp = row.original.createdAt;
        if (!timestamp) return 'N/A';
        
        // Handle Firebase Timestamp format
        if (timestamp.seconds) {
          const date = new Date(timestamp.seconds * 1000);
          return toLocalDateString(date);
        }
        
        // Fallback to cleanDateAndTimeObject for other formats
        const date = cleanDateAndTimeObject(timestamp, true);
        return date ? toLocalDateString(date) : 'N/A';
      },
    },
    // DED-1141: Removed "Linked To" column - Link to Audit removed from incidents
    {
      header: "Actions",
      cell: ({ row }) => {
        const incident = row.original;
        // DED-1141: Changed to pencil/bin buttons matching data register style
        return (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedIncident(incident);
                setEditDialogOpen(true);
              }}
              title="Edit"
            >
              <FontAwesomeIcon icon={faPenToSquare} className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                setIncidentToDelete(incident.id);
                setShowDeleteDialog(true);
              }}
              title="Delete"
              className="text-destructive hover:text-destructive"
            >
              <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle>Delete Incident</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this incident? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (incidentToDelete) {
                  try {
                    // Mark as deleted immediately (timestamped) to avoid transient prop re-insertion
                    setDeletedIds(prev => {
                      const next = new Map(prev);
                      next.set(incidentToDelete, Date.now());
                      return next;
                    });
                    // Optimistically update local table state
                    setIncidentList(list => list.filter(i => i.id !== incidentToDelete));
                    if (onDelete) {
                      await onDelete(incidentToDelete);
                    }
                  } catch (err) {
                    console.error('Failed to delete incident', err);
                  }
                }
                setShowDeleteDialog(false);
                setIncidentToDelete(null);
              }}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="flex justify-end mb-3">
        <Button onClick={() => setCreateDialogOpen(true)} variant="default">+ New Incident</Button>
      </div>
      <DataTable 
        columns={columns} 
        data={incidentList} 
        pageSize={5}
        columnDisplayConfig={incidentsColumnConfig}
        onRowClick={(row, e) => {
          // Don't handle row click if the click was on a button or anchor
          const target = e.target as HTMLElement;
          if (target.closest('button, a, [role="button"], [role="menuitem"], select, option')) {
            return;
          }
          setSelectedIncident(row);
          setDialogOpen(true);
        }} 
      />
      <IncidentDetailDialog open={dialogOpen} onOpenChange={setDialogOpen} incident={selectedIncident} />
      <IncidentCreateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreate={handleCreate}
        auditOptions={auditOptions}
      />
      <IncidentEditDialog
        open={editDialogOpen}
        auditOptions={auditOptions}
        onOpenChange={setEditDialogOpen}
        incident={selectedIncident}
        isSubmitting={isSubmitting}
        onSave={async (updates) => {
          if (!selectedIncident || !onIncidentUpdated) return;
          setIsSubmitting(true);
          try {
            const success = await onIncidentUpdated(selectedIncident.id, updates);
            if (success) {
              setIncidentList(list => list.map(i => i.id === selectedIncident.id ? { ...i, ...updates } : i));
              setEditDialogOpen(false);
            }
          } finally {
            setIsSubmitting(false);
          }
        }}
      />
    </>
  );
}

